import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from '@/components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import CleaningSchedule from './pages/CleaningSchedule';
import LoanedEquipment from './pages/LoanedEquipment';
import AdminPanel from './pages/AdminPanel';
import SystemAdmin from './pages/SystemAdmin';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import Montering from './pages/Montering';
import Layout from './components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const APP_VERSION = '2.1.0';

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cache-busting: check for new version and notify user
    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json?t=' + Date.now());
        const data = await res.json();
        const stored = localStorage.getItem('app_version');
        if (stored && stored !== data.version) {
          localStorage.setItem('app_version', data.version);
          // Clear caches and reload once
          if (!sessionStorage.getItem('version_reloaded')) {
            sessionStorage.setItem('version_reloaded', '1');
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map(k => caches.delete(k)));
            }
            window.location.reload();
            return;
          }
        }
        localStorage.setItem('app_version', data.version);
      } catch (err) { console.warn('[App]', err); }
    };
    checkVersion();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Only clear token on 401 (invalid/expired), not on network errors
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Global 401 interceptor — auto-logout on invalid token
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401 && localStorage.getItem('token')) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Laster...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to={user.role === 'lager' ? '/loaned-equipment' : user.role === 'kunde' ? '/kunde' : '/'} />} />
          <Route
            path="/*"
            element={
              user ? (
                <Layout>
                  <Routes>
                    {user.role !== 'lager' && <Route path="/" element={<Dashboard />} />}
                    {user.role !== 'lager' && (!user.page_access?.length || user.page_access.includes('/customers')) && <Route path="/customers" element={<Customers />} />}
                    {user.role !== 'lager' && (!user.page_access?.length || user.page_access.includes('/customers')) && <Route path="/customers/:id" element={<CustomerDetail />} />}
                    {user.role !== 'lager' && (!user.page_access?.length || user.page_access.includes('/cleaning')) && <Route path="/cleaning" element={<CleaningSchedule />} />}
                    {user.role !== 'lager' && (!user.page_access?.length || user.page_access.includes('/montering')) && <Route path="/montering" element={<Montering />} />}
                    {user.role !== 'lager' && (!user.page_access?.length || user.page_access.includes('/reports')) && <Route path="/reports" element={<Reports />} />}
                    <Route path="/loaned-equipment" element={<LoanedEquipment />} />
                    <Route path="/settings" element={<Settings />} />
                    {(user.role === 'admin' || user.role === 'system_admin') && (!user.page_access?.length || user.page_access.includes('/admin')) && <Route path="/admin" element={<AdminPanel />} />}
                    {user.role === 'system_admin' && <Route path="/system-admin" element={<SystemAdmin />} />}
                    <Route path="*" element={<Navigate to={user.role === 'lager' ? '/loaned-equipment' : '/'} />} />
                  </Routes>
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthContext.Provider>
  );
}

export default App;
